<template>
	<div class="sliderPane" v-focus role="region" :aria-label="sliderRegion" >
		<template v-if="isLoading">
			<div class="sliderPane-header" >
				<v-toolbar class="px-2" color="transparent" elevation="0">
					<v-spacer></v-spacer>
					<v-toolbar-items>
						<v-btn icon role="button" :aria-label="getCloseValue" @click="close">
							<v-icon>close</v-icon>
						</v-btn>
					</v-toolbar-items>
				</v-toolbar>
			</div>
			<v-row align-content="center" justify="center">
				<v-col cols="12" class="text-subtitle-1 text-center">
					{{loadingLabelValue}}
				</v-col>
				<v-col cols="6">
					<v-progress-linear color="secondary"
									   indeterminate
									   rounded
									   height="6" />
				</v-col>
			</v-row>
		</template>
		<template v-else>
			<div class="sliderPane-header">
				<v-toolbar aria-role="toolbar" class="px-2" color="transparent" elevation="0">
					<v-toolbar-title role="heading" aria-level="1">
						<slot name="label">{{label}}</slot>
						<slot name="sublabel">
							<div class="text-body-2" role="heading" aria-level="2" :title="sublabel" v-if="sublabel">{{sublabel}}</div>
						</slot>
					</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-toolbar-items role="navigation">
						<slot name="actions"></slot>
						<v-btn icon
							   text
                               role="button"
                               :aria-label="getCloseValue"
							   v-if="showClose"
                               @click="close">
							<v-icon>close</v-icon>
						</v-btn>
					</v-toolbar-items>
					<template #extension v-if="$slots.extension">
						<slot name="extension"></slot>
					</template>
				</v-toolbar>
				<v-divider />
			</div>
			<div class="sliderPane-body px-2">
				<slot></slot>
			</div>
			<div class="sliderPane-footer" v-if="showFooterComputed">
				<v-divider v-if="showFooterDivider"/>
				<slot name="footer">
					<div class="sliderPane-footer-content px-6 py-3">
            <v-btn class="sliderPane-footer-content-button"
                   depressed
                   :loading="getSaving(2)"
                   :disabled="getDisabled(2)"
                   color="primary"
                   type="submit"
                   role="button"
                   :aria-label="saveAndCloseLabelValue"
                   v-if="showSaveAndClose"
                   @click="saveAndClose">
              {{saveAndCloseLabelValue}}
            </v-btn>
						<v-btn class="sliderPane-footer-content-button"
							   depressed
							   :loading="getSaving(1)"
                 :disabled="getDisabled(1)"
							   color="primary"
							   type="submit"
							   v-if="showSave"
                               role="button"
                               :aria-label="saveLabelValue"
							   @click="save">
							{{saveLabelValue}}
						</v-btn>
						<v-btn class="sliderPane-footer-content-button"
							   depressed
							   :disabled="isSaving"
							   v-if="showCancel" role="button"
                               :aria-label="cancelLabelValue"
                               @click="cancel">
							{{cancelLabelValue}}
						</v-btn>

						<slot name="footerOther"></slot>
            <template v-if="showRemove">
              <v-spacer/>
              <v-btn color="error"
                     text
                     role="button"
                     :aria-label="removeLabelValue"
                     :disabled="getDisabled(3)"
                     @click="remove">
                {{removeLabelValue}}
              </v-btn>
            </template>

          </div>
				</slot>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Emit, Inject, Watch } from "vue-property-decorator";

function nullableBoolean(value?: boolean) {
	if (value === undefined)
		return;
	return Boolean(value);
}

@Component
export default class SliderPane extends Vue {
	@Prop({ type: Boolean }) showClose: boolean;
	@Prop({ type: Boolean, default: true }) showSave: boolean;
	@Prop({ type: Boolean, default: true }) showCancel: boolean;
  @Prop({ type: Boolean, default: true }) showFooterDivider: boolean;
  @Prop({ type: Boolean, default: false }) showSaveAndClose: boolean;
  @Prop({ type: Boolean, default: false }) showRemove: boolean;
	@Prop({ default: nullableBoolean }) showFooter: boolean;
	@Prop({ type: String }) saveLabel: string;
  @Prop({ type: String }) saveAndCloseLabel: string;
  @Prop({ type: String }) removeLabel: string;
	@Prop({ type: Boolean }) isLoading: boolean;
	@Prop({ type: Boolean }) isSaving: boolean;
	@Prop({ type: String }) loadingLabel: string;
	@Prop({ type: String }) cancelLabel: string;
	@Prop({ type: String }) label: string;
	@Prop({ type: String }) sublabel: string;

	@Prop({ type: Boolean, default: false }) showOverlay: boolean;
	@Prop({ default: null }) size: string;

  actionType: ActionType = ActionType.none;
  
	log = console.log;

	get loadingLabelValue() {
		return this.loadingLabel || `${this.$vopsui.labels.loading()}...`;
	}

    get getCloseValue() {
        return `${this.$vopsui.labels.close()}`;
    }

    get sliderRegion() {
        const label = this.label ? " " + this.label :"";
        return `${this.$vopsui.labels.slider()}${label}`;
    }

	get saveLabelValue() {
		return this.saveLabel || this.$vopsui.labels.save();
	}

  get saveAndCloseLabelValue() {
    return this.saveAndCloseLabel ||  this.$vopsui.labels.saveAndClose();
  }
  
	get cancelLabelValue() {
		return this.cancelLabel || this.$vopsui.labels.cancel();
	}

  get removeLabelValue() {
    return this.removeLabel || this.$vopsui.labels.remove();
  }
  
	created() {
		this.onSizeChanged();
	}

	destroyed() {}

	@Inject("closeSlider")
	close: () => void;

	@Inject("setSliderContentSize")
	setSliderContentSize: (size: string | number) => void;

	@Inject({ default: "setFormSaving" })
	setFormSaving: (isSaving: boolean) => void;

	@Watch("size")
	onSizeChanged() {
		this.setSliderContentSize(this.size);
	}

	@Watch("isSaving")
	onSavingChanged() {
  	if (this.setFormSaving)
			this.setFormSaving(this.isSaving);
	}

  getDisabled(action: ActionType) {
    return this.isSaving && this.actionType !== action;
  }
  
  getSaving(action: ActionType) {
    return this.isSaving && this.actionType === action;
  }

	get showFooterComputed() {
		const isUndefined = this.showFooter === undefined;
		if (this.showClose && isUndefined)
			return false;
		return isUndefined ? true : this.showFooter;
	}

	@Emit()
	save() {
    this.actionType = ActionType.save;
  }

  @Emit() 
  saveAndClose() {
    this.actionType = ActionType.saveAndClose ;
  }

  @Emit()
  remove() {
    this.actionType = ActionType.remove;
  }
  
	@Emit()
	cancel() {
		this.close();
	}
}

enum ActionType {
  none = 0,
  save = 1,
  saveAndClose = 2,
  remove = 3
}

</script>

<style lang="scss">
	.sliderPane {
		position: absolute;
		left: 0;
		top: 0;
		right: 0;
		bottom: 0;
		z-index: 0;

		display: flex;
		flex-direction: column;
		overflow: visible;
		height: 100%;
		max-width: 100%;
		width: 100%;
	}

	.sliderPane-body {
		overflow: auto;
		-webkit-overflow-scrolling: touch;
	}

	.sliderPane-header {
		border-top: solid 2px var(--v-secondary-base);
	}

	.sliderPane-footer-content {
		display: flex;
	}

	.sliderPane-footer-content-button {
		& + & {
			margin-left: 8px;
		}
	}

	.container {
		.sliderPane-body > & {
			padding: 16px;
		}
	}
</style>
